import React, { useState } from 'react';
import { AlertTriangle, Shield, Eye, EyeOff, X } from 'lucide-react';

interface PHIFlag {
  type: string;
  confidence: 'low' | 'medium' | 'high';
  location: string;
  snippet?: string;
  recommendedAction: 'review' | 'redact' | 'warn';
}

interface PHIAlertBannerProps {
  phiFlags: PHIFlag[];
  onClearFlag?: (index: number) => void;
  onRedact?: () => void;
  onDismiss?: () => void;
  className?: string;
}

export const PHIAlertBanner: React.FC<PHIAlertBannerProps> = ({
  phiFlags,
  onClearFlag,
  onRedact,
  onDismiss,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  if (!phiFlags || phiFlags.length === 0 || isDismissed) {
    return null;
  }

  const highConfidenceCount = phiFlags.filter(f => f.confidence === 'high').length;
  const mediumConfidenceCount = phiFlags.filter(f => f.confidence === 'medium').length;
  const requiresRedaction = phiFlags.some(f => f.recommendedAction === 'redact');

  const getBannerColor = () => {
    if (highConfidenceCount > 0 || requiresRedaction) {
      return 'bg-red-50 border-red-300 text-red-800';
    }
    if (mediumConfidenceCount > 0) {
      return 'bg-yellow-50 border-yellow-300 text-yellow-800';
    }
    return 'bg-blue-50 border-blue-300 text-blue-800';
  };

  const getIconColor = () => {
    if (highConfidenceCount > 0 || requiresRedaction) {
      return 'text-red-600';
    }
    if (mediumConfidenceCount > 0) {
      return 'text-yellow-600';
    }
    return 'text-blue-600';
  };

  const getTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      ssn: 'Social Security Number',
      phoneNumber: 'Phone Number',
      email: 'Email Address',
      dateOfBirth: 'Date of Birth',
      medicalRecordNumber: 'Medical Record Number',
      healthInsuranceNumber: 'Health Insurance Number',
      accountNumber: 'Account Number',
      name: 'Name',
      address: 'Address',
      healthRecord: 'Health Record Information',
      ipAddress: 'IP Address',
    };
    return labels[type] || type;
  };

  const getConfidenceBadge = (confidence: string) => {
    const colors: Record<string, string> = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-blue-100 text-blue-800 border-blue-200',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colors[confidence]}`}>
        {confidence.toUpperCase()}
      </span>
    );
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <div className={`border-2 rounded-lg ${getBannerColor()} ${className}`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className={`mt-0.5 ${getIconColor()}`}>
              {requiresRedaction ? (
                <Shield className="w-6 h-6" />
              ) : (
                <AlertTriangle className="w-6 h-6" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold flex items-center space-x-2">
                <span>Protected Health Information (PHI) Detected</span>
              </h3>
              <p className="text-sm mt-1 opacity-90">
                {phiFlags.length} potential PHI {phiFlags.length === 1 ? 'item' : 'items'} detected
                {highConfidenceCount > 0 && (
                  <span className="font-semibold"> ({highConfidenceCount} high confidence)</span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-black hover:bg-opacity-10 rounded transition-colors"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-black hover:bg-opacity-10 rounded transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Warning Message */}
        {requiresRedaction && (
          <div className="mt-3 p-3 bg-red-100 bg-opacity-50 rounded border border-red-300 border-opacity-50">
            <p className="text-sm font-medium">
              ⚠️ High-confidence PHI detected. Review and redact before sharing or processing further.
            </p>
          </div>
        )}

        {/* Expanded Details */}
        {isExpanded && (
          <div className="mt-4 space-y-3">
            {phiFlags.map((flag, index) => (
              <div
                key={index}
                className="bg-white bg-opacity-60 rounded-lg p-3 border border-current border-opacity-20"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="font-semibold">{getTypeLabel(flag.type)}</span>
                      {getConfidenceBadge(flag.confidence)}
                    </div>
                    <div className="text-sm space-y-1">
                      <div>
                        <span className="font-medium">Location:</span> {flag.location}
                      </div>
                      <div>
                        <span className="font-medium">Recommended Action:</span>{' '}
                        <span className="capitalize font-semibold">{flag.recommendedAction}</span>
                      </div>
                      {flag.snippet && (
                        <div>
                          <span className="font-medium">Sample:</span>{' '}
                          <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">
                            {flag.snippet}
                          </code>
                        </div>
                      )}
                    </div>
                  </div>

                  {onClearFlag && (
                    <button
                      onClick={() => onClearFlag(index)}
                      className="ml-3 text-sm text-current opacity-75 hover:opacity-100 underline"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-4 flex items-center space-x-3">
          {!isExpanded && (
            <button
              onClick={() => setIsExpanded(true)}
              className="px-4 py-2 bg-white bg-opacity-60 hover:bg-opacity-80 rounded-lg font-medium text-sm border border-current border-opacity-30 transition-colors"
            >
              Review Details
            </button>
          )}
          {onRedact && requiresRedaction && (
            <button
              onClick={onRedact}
              className="px-4 py-2 bg-white bg-opacity-60 hover:bg-opacity-80 rounded-lg font-medium text-sm border border-current border-opacity-30 transition-colors"
            >
              Redact PHI
            </button>
          )}
        </div>

        {/* HIPAA Notice */}
        <div className="mt-4 text-xs opacity-75 border-t border-current border-opacity-20 pt-3">
          <p>
            <strong>HIPAA Compliance Notice:</strong> This content may contain Protected Health Information (PHI).
            Ensure proper handling according to HIPAA regulations and your organization's privacy policies.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PHIAlertBanner;
